import { useEffect } from 'react';
import { useUiStore } from '@/store';

/**
 * Takes the dispatcher to what the notification was about.
 *
 * The service worker focuses an already-open tab rather than opening a
 * second one (see sw.js's notificationclick), which is right — but a focused
 * tab is still showing whatever view was left open, usually not the one the
 * alert meant. So the worker posts the target alongside the focus, and this
 * applies it.
 *
 * The mapping is from a URL to a view id because the worker deals in URLs:
 * it may also have had to openWindow() on a cold start, where the path is
 * the only thing that can carry the destination.
 */
const PATH_TO_VIEW = {
  '/approvals': 'approvals',
  '/orders': 'orders',
  '/alerts': 'alerts',
  '/fleet': 'fleet',
  '/drivers': 'drivers',
};

export function usePushNavigation() {
  const setView = useUiStore((s) => s.setView);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;

    const onMessage = (event) => {
      if (event.data?.type !== 'PUSH_NAVIGATE') return;
      const raw = event.data.url || '/';
      let pathname = raw;
      try {
        pathname = new URL(raw, window.location.origin).pathname;
      } catch {
        // A malformed url from a payload is not worth throwing over; fall
        // through and simply do not navigate.
      }
      const view = PATH_TO_VIEW[pathname.replace(/\/+$/, '')];
      if (view) setView(view);
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [setView]);
}
